import { NOT_CONNECTED, READY } from '@/lib/ops-matrix.js'
import { H, PLOT, W, areaPath, bandPath, bandTicks, dayIndexAt, gapColumns, isolated, line, longDate, shortDate, singleSeries, stackedSeries, unprovenColumns, xAt, yAt } from './chart-model.js'

const TONES = ['var(--accent)', 'var(--info)', 'var(--success)', 'var(--warning)', 'var(--error)']
export const toneOf = i => (i < TONES.length ? TONES[i] : `color-mix(in srgb, ${TONES[i % TONES.length]} 55%, var(--foreground))`)

export const CHART_COPY = {
  allTitle: 'Sent per day, every campaign',
  singleTitle: 'Sends and replies',
  notConnected: 'Sends and replies per day are not connected yet.',
  notConnectedHint: 'No route records sends or total replies, so nothing is drawn. Held replies are not total replies.',
  unavailable: 'Sends and replies per day could not be loaded.',
  noCampaign: 'This campaign has no days in the series.',
  none: 'Every campaign is hidden. Show one to draw the chart.',
  empty: days => `No sends or replies recorded in the last ${days || 'selected'} days.`,
  unproven: 'Not proven: days with unconfirmed sends.',
  allWorkspaces: 'All workspaces',
  coversAll: 'Counts cover all workspaces',
}

const mono = 'eh-mono'
const MONO = 'JetBrains Mono, ui-monospace, monospace'
const fmt = v => (v === null ? 'n/a' : v)
const GAP = 'Gap: a day with no recorded count.'
// A campaign on a day with unknown sends: never a plain number. The sends already confirmed that day are said apart.
const sentWord = d => (d.unproven ? `not proven${d.confirmed === null ? '' : ` (${d.confirmed} confirmed)`}` : fmt(d.sent))
const totalWord = (value, unprovenDays) => (value !== null ? value : unprovenDays ? 'not proven' : 'n/a')
const HATCH = 'eh-unproven-hatch'

const Hatch = () => (
  <pattern id={HATCH} width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
    <line x1="0" y1="0" x2="0" y2="6" stroke="var(--eh-amber)" strokeWidth="2" />
  </pattern>
)
const Unproven = ({ x }) => <rect data-slot="unproven-marker" x={x - 6} y={PLOT.y0} width="12" height={PLOT.y1 - PLOT.y0} rx="2" fill={`url(#${HATCH})`} fillOpacity="0.6" stroke="var(--eh-amber)" strokeDasharray="2 2" strokeOpacity="0.8" />
const Empty = ({ days }) => <div data-slot="chart-region" data-state="empty" role="status" className="eh-gap"><span className="eh-gap-title">{CHART_COPY.empty(days)}</span></div>

function Gap({ cell, label }) {
  const bad = cell.kind !== NOT_CONNECTED
  return (
    <div data-slot="chart-region" data-state={bad ? 'unavailable' : 'notConnected'} role={bad ? 'status' : undefined} title={bad ? undefined : cell.needs} className="eh-gap">
      <span className="eh-gap-title">{bad ? CHART_COPY.unavailable : CHART_COPY.notConnected}</span>
      {!bad && <span className="eh-note">{CHART_COPY.notConnectedHint}</span>}
      <span className="sr-only">{label}</span>
    </div>
  )
}

function Axis({ maxLeft, maxRight, days, rightTone }) {
  const n = days.length
  const ticks = [0, Math.floor((n - 1) / 3), Math.floor(((n - 1) * 2) / 3), n - 1].filter((v, i, a) => a.indexOf(v) === i)
  return (
    <g>
      {[0, 0.5, 1].map(f => <line key={f} x1={PLOT.x0} x2={PLOT.x1} y1={yAt(maxLeft * f, maxLeft)} y2={yAt(maxLeft * f, maxLeft)} stroke={f === 0 ? 'var(--eh-grid-base)' : 'var(--eh-grid)'} />)}
      {[1, 0.5, 0].map(f => <text key={f} x={PLOT.x0 - 6} y={yAt(maxLeft * f, maxLeft) + 4} textAnchor="end" fontSize="10" fontFamily={MONO} fill="var(--eh-dim)">{maxLeft * f}</text>)}
      {maxRight != null && [1, 0.5].map(f => <text key={f} x={PLOT.x1 + 6} y={yAt(maxRight * f, maxRight) + 4} fontSize="10" fontFamily={MONO} fill={rightTone}>{maxRight * f}</text>)}
      {ticks.map((i, k) => <text key={i} x={xAt(i, n)} y={H - 8} textAnchor={k === 0 ? 'start' : k === ticks.length - 1 ? 'end' : 'middle'} fontSize="10" fill="var(--eh-dim)">{shortDate(days[i].date)}</text>)}
    </g>
  )
}

function Frame({ aria, onMove, onLeave, onKey, children, readout }) {
  return (
    <div data-slot="chart" className="eh-chart" onMouseMove={onMove} onMouseLeave={onLeave}>
      <div tabIndex={0} role="group" aria-label={`${aria}. Arrow keys move through the days.`} onKeyDown={onKey} onBlur={onLeave}
        className="rounded-lg outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--accent)">
        <svg viewBox={`0 0 ${W} ${H}`} className="block h-auto w-full" role="img" aria-label={aria}>{children}</svg>
      </div>
      {readout}
    </div>
  )
}

const Readout = ({ idx, n, children }) => (
  <div data-slot="readout" role="status" style={{ left: `${(xAt(idx, n) / W) * 100}%`, transform: `translateX(${idx > n * 0.7 ? '-85%' : idx < n * 0.3 ? '-15%' : '-50%'})` }} className="eh-tip">
    {children}
  </div>
)

const Line = ({ label, value, tone }) => (
  <div className="flex items-center justify-between gap-3">
    <span className="inline-flex min-w-0 items-center gap-1.5">{tone && <span aria-hidden="true" className="inline-block size-2 shrink-0 rounded-sm" style={{ background: tone }} />}<span className="truncate">{label}</span></span>
    <span className={mono}>{value}</span>
  </div>
)

// The last day that has a reply count gets the artboard's end dot.
const endDot = (pts, n) => { const last = pts.length ? pts[n - 1] : null; return last ? <circle data-slot="end-dot" cx={last[0]} cy={last[1]} r="4" fill="var(--eh-amber)" /> : null }

// One campaign: sends as an area on the left scale, replies as a line on the right scale. The crosshair follows the pointer.
export function SingleChart({ series, campaign, tone, hover, onHover, name, allWorkspaces = false }) {
  if (series.kind !== READY) return <Gap cell={series} label={name} />
  if (series.value.days.length === 0) return <Empty days={series.value.span} />
  const c = singleSeries(series.value, campaign)
  if (!series.value.days.some(d => d.sent[campaign] != null || d.replies[campaign] != null || d.unknown?.[campaign] > 0)) {
    return <div data-slot="chart-region" data-state="unavailable" role="status" className="eh-gap">{CHART_COPY.noCampaign}</div>
  }
  const n = c.days.length
  const idx = hover == null ? null : Math.min(hover, n - 1)
  const area = areaPath(c.sent)
  const gaps = c.totalSent === null || c.totalReplies === null
  const d = idx == null ? null : c.days[idx]
  const move = e => { const r = e.currentTarget.getBoundingClientRect(); onHover(dayIndexAt((e.clientX - r.left) / r.width, n)) }
  const key = e => {
    if (e.key === 'Escape') return onHover(null)
    const step = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0
    if (!step) return
    e.preventDefault()
    onHover(Math.min(n - 1, Math.max(0, (idx ?? (step > 0 ? -1 : n)) + step)))
  }
  return (
    <>
      <div data-slot="legend" className="eh-chart-head">
        <span className="eh-key"><span aria-hidden="true" className="inline-block h-2 w-3 rounded-sm" style={{ background: 'var(--eh-area-line)', opacity: 0.6 }} />sent per day (left)</span>
        <span className="eh-key"><span aria-hidden="true" className="inline-block h-0.5 w-3" style={{ background: 'var(--eh-amber)' }} />replies per day (right)</span>
        {c.unprovenDays > 0 && <span className="eh-key"><span aria-hidden="true" className="eh-hatch-key" />not proven</span>}
        {gaps && <span data-slot="gap-note" data-state="partial">{GAP}</span>}
        {c.unprovenDays > 0 && <span data-slot="unproven-note" data-state="partial">{CHART_COPY.unproven}</span>}
        {allWorkspaces && <span data-slot="scope-note" className="eh-note">{CHART_COPY.allWorkspaces}</span>}
      </div>
      <Frame aria={`Sends and replies for ${name} over ${n} days, ${c.totalSent === null ? (c.unprovenDays ? 'sent total not proven' : 'sent total incomplete') : `${c.totalSent} sent`} and ${c.totalReplies === null ? 'reply total incomplete' : `${c.totalReplies} replies`}`} onMove={move} onLeave={() => onHover(null)} onKey={key}
        readout={d && <Readout idx={idx} n={n}>{allWorkspaces && <div data-slot="readout-scope" className="eh-note">{CHART_COPY.coversAll}</div>}<div className="font-semibold">{longDate(d.date)}</div><Line label="sent" value={sentWord(d)} tone="var(--eh-area-line)" /><Line label="replies" value={fmt(d.replies)} tone="var(--eh-amber)" />{d.held > 0 && <Line label="held, not replies" value={d.held} />}</Readout>}>
        <defs><Hatch /><linearGradient id="eh-send-fill" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="var(--eh-area-top)" /><stop offset="100%" stopColor="var(--eh-area-bottom)" /></linearGradient></defs>
        <Axis maxLeft={c.maxS} maxRight={c.maxR} days={c.days} rightTone="var(--eh-amber)" />
        {c.days.map((d, i) => (d.unproven ? <Unproven key={d.date} x={xAt(i, n)} /> : null))}
        <path d={area} fill="url(#eh-send-fill)" />
        <path d={line(c.sent)} fill="none" stroke="var(--eh-area-line)" strokeWidth="2" />
        <path d={line(c.replies)} fill="none" stroke="var(--eh-amber)" strokeWidth="2" strokeLinejoin="round" />
        {isolated(c.sent).map(([x, y]) => <circle key={`s${x}`} data-slot="point" data-series="sent" cx={x} cy={y} r="3" fill="var(--eh-area-line)" />)}
        {isolated(c.replies).map(([x, y]) => <circle key={`r${x}`} data-slot="point" data-series="replies" cx={x} cy={y} r="3" fill="var(--eh-amber)" />)}
        {endDot(c.replies, n)}
        {idx != null && (
          <g data-slot="crosshair">
            <line x1={xAt(idx, n)} x2={xAt(idx, n)} y1={PLOT.y0} y2={PLOT.y1} stroke="var(--eh-cursor)" strokeDasharray="3 3" />
            {c.sent[idx] && <circle cx={xAt(idx, n)} cy={c.sent[idx][1]} r="5" fill="var(--eh-dot-fill)" stroke="var(--eh-area-line)" strokeWidth="2" />}
            {c.replies[idx] && <circle cx={xAt(idx, n)} cy={c.replies[idx][1]} r="5" fill="var(--eh-dot-fill)" stroke="var(--eh-amber)" strokeWidth="2" />}
          </g>
        )}
      </Frame>
    </>
  )
}

// Every campaign stacked. The legend hides and shows a campaign; totals and the readout follow the visible ones.
export function AllChart({ series, campaigns, hidden, onToggle, hover, onHover, allWorkspaces = false }) {
  if (series.kind !== READY) {
    const bad = series.kind !== NOT_CONNECTED
    return (
      <>
        <div data-slot="legend" data-state={bad ? 'unavailable' : 'notConnected'} className="eh-note">Legend and day readout: {bad ? 'unavailable' : 'not connected'}</div>
        <Gap cell={series} label="Every campaign" />
      </>
    )
  }
  if (series.value.days.length === 0) return <Empty days={series.value.span} />
  const ids = campaigns.map(c => c.id)
  const hide = new Set(hidden)
  const st = stackedSeries(series.value, ids, hide)
  const n = st.days.length
  const idx = hover == null ? null : Math.min(hover, n - 1)
  const day = idx == null ? null : st.days[idx]
  const move = e => { const r = e.currentTarget.getBoundingClientRect(); onHover(dayIndexAt((e.clientX - r.left) / r.width, n)) }
  const key = e => {
    if (e.key === 'Escape') return onHover(null)
    const step = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0
    if (!step) return
    e.preventDefault()
    onHover(Math.min(n - 1, Math.max(0, (idx ?? (step > 0 ? -1 : n)) + step)))
  }
  const shownSent = day ? totalWord(day.total, day.unproven ? 1 : 0) : totalWord(st.totalSent, st.unprovenDays)
  const shownReplies = fmt(day ? day.replies : st.totalReplies)
  const gaps = st.days.some(d => (d.total === null && !d.unproven) || d.replies === null)
  return (
    <>
      <div data-slot="totals" aria-label="Totals across the visible campaigns" className="eh-totals">
        <div className="eh-total"><span className="eh-label">{day ? `Sent ${longDate(day.date)}` : 'Sent, all days'}</span><span data-slot="total-sent" className={mono}>{shownSent}</span></div>
        <div className="eh-total"><span className="eh-label">{day ? `Replies ${longDate(day.date)}` : 'Replies, all days'}</span><span data-slot="total-replies" className={mono}>{shownReplies}</span></div>
      </div>
      {allWorkspaces && <p data-slot="scope-note" className="eh-note">{CHART_COPY.allWorkspaces}</p>}
      {(gaps || st.unprovenDays > 0) && (
        <p className="eh-note">
          {st.unprovenDays > 0 && <span data-slot="unproven-note" data-state="partial">{CHART_COPY.unproven}</span>}{st.unprovenDays > 0 && gaps && ' '}
          {gaps && <span data-slot="gap-note" data-state="partial">{GAP}</span>}
        </p>
      )}
      <div data-slot="legend" role="group" aria-label="Campaigns shown in the chart" className="eh-legend">
        {campaigns.map((c, i) => {
          const on = !hide.has(c.id)
          return (
            <button key={c.id} type="button" data-slot="legend-item" aria-pressed={on} onClick={() => onToggle(c.id)}
              className="eh-legend-item min-h-11">
              <span aria-hidden="true" className="inline-block size-2.5 shrink-0 rounded-sm" style={{ background: on ? toneOf(i) : 'transparent', border: `1.5px solid ${toneOf(i)}` }} />
              <span className="break-words [overflow-wrap:anywhere]">{c.name}</span>
            </button>
          )
        })}
      </div>
      {st.visible.length === 0 ? (
        <div data-slot="chart-region" data-state="empty" role="status" className="eh-gap">{CHART_COPY.none}</div>
      ) : (
        <Frame aria={`Stacked daily sends for ${st.visible.length} of ${ids.length} campaigns over ${n} days, ${st.totalSent === null ? (st.unprovenDays ? 'sent total not proven' : 'sent total incomplete') : `${st.totalSent} sent`} and ${st.totalReplies === null ? 'reply total incomplete' : `${st.totalReplies} replies`}`} onMove={move} onLeave={() => onHover(null)} onKey={key}
          readout={day && (
            <Readout idx={idx} n={n}>
              {allWorkspaces && <div data-slot="readout-scope" className="eh-note">{CHART_COPY.coversAll}</div>}
              <div className="font-semibold">{longDate(day.date)}</div>
              {day.layers.map(l => <Line key={l.id} label={campaigns.find(c => c.id === l.id).name} value={sentWord({ unproven: l.unproven, confirmed: l.confirmed, sent: l.value })} tone={toneOf(ids.indexOf(l.id))} />)}
              <div className="mt-1 border-t border-(--eh-grid-base) pt-1"><Line label="Total sent" value={totalWord(day.total, day.unproven ? 1 : 0)} /><Line label="Replies" value={fmt(day.replies)} />{day.held > 0 && <Line label="Held, not replies" value={day.held} />}</div>
            </Readout>
          )}>
          <defs><Hatch /></defs>
          <Axis maxLeft={st.max} days={st.days} />
          {st.visible.map(id => (
            <g key={id} data-slot="band" data-campaign={id}>
              <path d={bandPath(st, id)} fill={toneOf(ids.indexOf(id))} fillOpacity="0.78" stroke="var(--eh-dot-fill)" strokeWidth="0.5" />
              <path d={bandTicks(st, id)} fill="none" stroke={toneOf(ids.indexOf(id))} strokeWidth="3" />
            </g>
          ))}
          {unprovenColumns(st).map(x => <Unproven key={x} x={x} />)}
          {gapColumns(st).map(x => <line key={x} data-slot="gap-marker" x1={x} x2={x} y1={PLOT.y0} y2={PLOT.y1} stroke="var(--eh-dim)" strokeDasharray="2 3" />)}
          {day && (
            <g data-slot="crosshair">
              <line x1={xAt(idx, n)} x2={xAt(idx, n)} y1={PLOT.y0} y2={PLOT.y1} stroke="var(--eh-cursor)" />
              {day.total !== null && <circle cx={xAt(idx, n)} cy={yAt(day.total, st.max)} r="5" fill="var(--eh-dot-fill)" stroke="var(--eh-text)" strokeWidth="2" />}
            </g>
          )}
        </Frame>
      )}
    </>
  )
}
